import type React from 'react';
import { useTranslation } from 'react-i18next';
import { useSignaturesContext } from '../../../providers/SignaturesProvider';
import type { Dimensions, Position } from '../../../shared/interfaces';
import { getSignatureColor, SignatureBaseButton } from '../../../shared/components/SignatureBaseButton';
import type { AssignedSignee } from 'services/entities/SignatureTypes';
import type { BlockConfig } from '../../models/BlockConfig.model';
import { useInViewport } from '../../useInViewport';
import { useDraggableBlockManipulations } from '../../DraggableGridBlock/useDraggableBlockManipulations';
import './Signature.less';
import { GridBlockType } from 'services/entities/BlockTypes';
import { useSectionData } from 'components/editor/Sections/useSectionContext';
import { useKeyboardShortcuts } from 'components/editor/hooks/useKeyboardShortcuts';
import { useSignatureKeyboardShortcuts } from './useSignatureKeyboardShortcuts';

import { DraggableGridBlockWrapper } from '../../DraggableGridBlock/DraggableGridBlockWrapper';
import { useAppSelector } from 'components/editor/grid/reduxStore/Store';
import { selectBlockContent } from 'components/editor/grid/reduxStore/editorSlice';
import { signatureBlockTestIds } from './SignatureBlock.test-ids';

export interface UnSignedSignatureProps {
  signatureId: string;
  assignedSignee?: AssignedSignee | null;
  handleClick?: (e) => void;
  position: Position;
  dimensions: Dimensions;
  bounds: string;
}

const SignatureBlock: React.FunctionComponent<UnSignedSignatureProps> = ({
  signatureId,
  assignedSignee = null,
  position,
  dimensions,
  handleClick,
  bounds,
}) => {
  const { t } = useTranslation();
  const { handleSignatureRemoval, selectedSignature: contextSelectedSignature } = useSignaturesContext();
  const signee = assignedSignee;
  const { ref: scrollElRef } = useInViewport();
  const { handleSignatureKeyboardMove } = useDraggableBlockManipulations();
  const { blocksContent, sectionId } = useSectionData();
  const elementToBound = document.querySelector(bounds) as HTMLElement;
  const isSignatureSelected = contextSelectedSignature?.signatureId === signatureId;

  const onSignatureKeyboardMove = (deltaX: number, deltaY: number) => {
    const { x, y } = position;
    const { width, height } = dimensions;
    let newX = x + deltaX;
    let newY = y + deltaY;

    if (elementToBound) {
      const boundsRect = elementToBound.getBoundingClientRect();
      const canvasWidth = boundsRect.width;

      newX = Math.max(0, Math.min(newX, canvasWidth - width));
      if (newY < 0) {
        newY = 0;
      }
    }

    const blockConfig: BlockConfig = {
      height,
      width,
      id: signatureId,
      x: newX,
      y: newY,
      z: 1,
    };

    handleSignatureKeyboardMove({
      blockConfig,
    });
  };

  const keyboardShortcuts = useSignatureKeyboardShortcuts({
    isSignatureSelected,
    onSignatureKeyboardMove,
    onSignatureDelete: () => handleSignatureRemoval({ sectionId, signatureId }, blocksContent),
  });

  useKeyboardShortcuts(isSignatureSelected ? keyboardShortcuts : []);

  const reduxBlockContent = useAppSelector((state) => selectBlockContent(state, sectionId, signatureId));

  const blockConfig: BlockConfig = {
    ...dimensions,
    id: signatureId,
    ...position,
    z: reduxBlockContent?.blockConfig?.z ?? blocksContent[signatureId]?.blockConfig?.z ?? 1,
  };

  return (
    <DraggableGridBlockWrapper
      blockId={signatureId}
      blockConfig={blockConfig}
      blockType={GridBlockType.SIGNATURE}
      onMouseDown={handleClick}
      elementToBound={elementToBound}
      lockAspectRatio={true}
    >
      <div ref={scrollElRef} />
      <SignatureBaseButton
        color={getSignatureColor(signee?.email)}
        data-signature-id={signatureId}
        data-testid={signatureBlockTestIds.block}
      >
        {t('signature')}
      </SignatureBaseButton>
    </DraggableGridBlockWrapper>
  );
};

export default SignatureBlock;
